import { useModal } from "@hooks";
import { trackEvent } from "@lib/infrastructure";
import { getAllConnectedUsers, getAppLanguage, getUser, useAppSelector } from "@lib/store";
import { LocalDateString, OfficeWorkday, WorkspaceReservation } from "@lib/store/src/data/timeslots/apiTypes";
import { constructConnectionsOverview } from "@lib/store/src/data/timeslots/viewmodel-helpers/constructConnectionsOverview";
import { useConnectionsSchedule } from "@lib/store/src/ui/hooks/timeslots/useConnectionsSchedule";
import { useReservationDetails } from "@lib/store/src/ui/hooks/timeslots/useReservationDetails";
import { ModalCell, UserCell } from "@molecules";
import { Div, FlexCol, FlexRow, H4, P, Subtitle } from "@quarks";
import { format, parseISO } from "date-fns";
import { IconNames } from "mapiq-atoms";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { StyledModalPage } from "./styles";
import { Footer } from "./Footer";
import { FullMapView } from "./FullMapView";
import { MapPreviewCard } from "./MapPreviewCard";
import { AttendeeCell } from "./MemberCell";
import { formatDate } from "@lib/utils";
import { isFullDay, timeLabel } from "@lib/store/src/data/timeslots/dateTimeHelpers";
import { cn } from "@utils";

// Work in progress
export const BookingDetailsCard = ({
  date: dateKey,
  workday,
  workspaceReservation,
}: {
  date: LocalDateString;
  workday: OfficeWorkday | null;
  workspaceReservation: WorkspaceReservation;
}) => {
  const [inMapView, setInMapView] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { t } = useTranslation();
  const { closeModal } = useModal();
  const appLanguage = useAppSelector(getAppLanguage);

  const currentUserId = useAppSelector(getUser).id;

  const reservationsGroup = useReservationDetails(workspaceReservation);
  const connectedUsers = useAppSelector(getAllConnectedUsers);
  const connectionSchedule = useConnectionsSchedule(dateKey);

  const overview = constructConnectionsOverview({
    connectedUsers,
    connectionWorkspaceReservations: connectionSchedule.workspaceResevations,
    referenceLocation: workspaceReservation.workspace,
  });

  const workspace = workspaceReservation.workspace;

  const isInvitation = workspaceReservation.invitationTo !== null;
  const isGroupBooking = workspaceReservation.hasInvitations;
  const bookedByUser = workspaceReservation.organizer.id !== currentUserId ? workspaceReservation.organizer.name : null;

  const latestDate =
    new Date(workspaceReservation.createdAt) > new Date(workspaceReservation.lastUpdatedAt)
      ? workspaceReservation.createdAt
      : workspaceReservation.lastUpdatedAt;

  const modalTitle = isInvitation
    ? t("screen:WorkspaceInvitationDetails")
    : isGroupBooking
    ? t("workplace:GroupBooking")
    : t("screen:DeskDetail");

  const title =
    workspace.nodeType === "Desk" || workspace.nodeType === "Area"
      ? workspace.areaName
      : workspace.nodeType === "Floor"
      ? workspace.floorName
      : workspace.buildingName;

  const subtitle =
    workspace.nodeType === "Building"
      ? ""
      : workspace.nodeType === "Floor"
      ? workspace.buildingName
      : `${workspace.buildingName}, ${workspace.floorName}`;

  const icon: IconNames = workspace.nodeType === "Desk" ? "desk" : workspace.nodeType === "Area" ? "area" : "floor";

  const connectionOverviewTitle = isGroupBooking
    ? t("connection:OtherConnectionsWorkingInBuilding", {
        building: workspace.buildingName,
      })
    : t("connection:ConnectionsWorkingInBuilding", {
        building: workspace.buildingName,
      });

  return (
    <StyledModalPage
      title={modalTitle}
      onBack={inMapView ? () => setInMapView(false) : undefined}
      onClose={() => {
        closeModal();
        trackEvent("CalendarOverview_BookingDetails__Close", {
          date: dateKey,
          isGroupBooking,
        });
      }}
      bottom={
        <Footer
          workday={workday}
          workspaceReservation={workspaceReservation}
          isSaving={isSaving}
          setIsSaving={setIsSaving}
        />
      }>
      {inMapView && reservationsGroup.status === "fulfilled" && (
        <FullMapView
          title={title}
          subtitle={subtitle}
          icon={icon}
          attendees={reservationsGroup.data}
        />
      )}

      {!inMapView && (
        <>
          <ModalCell className="py-6">
            <MapPreviewCard
              workspaceReservation={workspaceReservation}
              members={reservationsGroup.status === "fulfilled" ? reservationsGroup.data : []}
              onClick={() => setInMapView(true)}
            />
          </ModalCell>

          <ModalCell className="pb-6">
            <FlexRow alignItems="flex-start">
              <FlexCol gap={4}>
                <H4 as="h2">{title}</H4>
                <P>{subtitle}</P>
                <P>
                  <>
                    {format(new Date(workspaceReservation.localStart), "EEEE d MMMM")},{" "}
                    {timeLabel(workspaceReservation?.localStart, workspaceReservation?.localEnd, t)}
                  </>
                </P>
              </FlexCol>
            </FlexRow>
          </ModalCell>

          {(isInvitation || isGroupBooking) && (
            <ModalCell className="border-t py-6">
              {reservationsGroup.status === "fulfilled" ? (
                reservationsGroup.data.map((member, i) => (
                  <AttendeeCell
                    attendee={member}
                    key={i}
                  />
                ))
              ) : (
                <h2>LOADING</h2>
              )}
            </ModalCell>
          )}

          {bookedByUser && !isInvitation && !isGroupBooking && (
            <ModalCell className="border-t py-6">
              <FlexCol gap={8}>
                <H4 as="h2">{t("workplace:BookedForYou")}</H4>
                <Subtitle>
                  {t("workplace:BookedByUserOnDate", {
                    user: bookedByUser,
                    date: formatDate(parseISO(latestDate), "dd/MM/yyyy", appLanguage),
                  })}
                </Subtitle>
              </FlexCol>
            </ModalCell>
          )}

          {overview.length > 0 && (
            <ModalCell className="border-t py-6">
              <H4
                as="h2"
                className="mb-3">
                {connectionOverviewTitle}
              </H4>

              {overview.map((o, i) => {
                return (
                  <Div
                    key={i}
                    className="mb-2">
                    <P
                      fontSize="sm"
                      fontWeight="bold">
                      {o.isUserWorkspace ? t("connection:YourLocation") : o.locationName}
                    </P>

                    {o.occupants.map(({ user, reservations }) => {
                      const workspaceLabel = reservations
                        .map(r => {
                          const deskName =
                            "deskName" in r.workspace && r.workspace.deskName ? r.workspace.deskName : null;
                          const time = isFullDay(r.localStart, r.localEnd)
                            ? null
                            : timeLabel(r.localStart, r.localEnd, t);

                          if (!deskName && !time) return undefined;

                          const label = [deskName, time && `(${time})`].filter(Boolean).join(" ");

                          return (
                            <p
                              key={r.id}
                              className="text-mapiq-black-500">
                              {label}
                            </p>
                          );
                        })
                        .filter(r => r !== undefined);

                      return (
                        <UserCell
                          separator={false}
                          padding="small"
                          key={user.id}
                          user={user}
                          subtitle={workspaceLabel}
                          className={cn({
                            "items-start": workspaceLabel.length > 0,
                          })}
                        />
                      );
                    })}
                  </Div>
                );
              })}
            </ModalCell>
          )}
        </>
      )}
    </StyledModalPage>
  );
};
